import { useMemo, useState } from 'react'
import { api } from '../lib/api'

// Add a batch of staff from a pasted list — names and emails copied from an
// email, Teams or a spreadsheet — instead of one form each. Every line is
// read into a row: usable when it has a first name, last name and email, a
// red chip with the reason when it doesn't. The template's variables (the
// topic, say) have one "same for everyone" value, and each usable row shows
// its own field to change it for that person; a column pasted after the
// email fills it too. "Add n people" creates each person with the same call
// the single form makes (POST /spaces/{id}/staff), one after another, and
// reports anyone the server refused.

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/

// One line → { first_name, last_name, email, extras } or { error }. Accepts
// "Jane Doe <jane@x.com>", "Jane Doe, jane@x.com", "Jane, Doe, jane@x.com",
// tab-separated cells, or the name and the email with only spaces between.
// Anything after the email in a delimited line — "…, jane@x.com, Month-end
// close" — comes back in `extras`, in order, for the template's variables.
export function parseLine(line) {
  const text = line.trim()
  if (!text) return null
  let name = ''
  let email = ''
  let extras = []
  const cells = (value) =>
    value
      .split(/[,;\t]/)
      .map((part) => part.trim())
      .filter(Boolean)
  const angled = text.match(/^(.*?)\s*<([^<>]+)>\s*(?:[,;\t]\s*(.*))?$/)
  if (angled) {
    name = angled[1]
    email = angled[2]
    extras = angled[3] ? cells(angled[3]) : []
  } else {
    const parts = cells(text)
    const tokens = parts.length > 1 ? parts : text.split(/\s+/)
    const emailIndex = tokens.findIndex((token) => token.includes('@'))
    if (emailIndex >= 0) {
      email = tokens[emailIndex]
      name = tokens.slice(0, emailIndex).join(' ')
      extras = parts.length > 1 ? tokens.slice(emailIndex + 1) : []
      // Name after the email ("jane@x.com, Jane Doe"): take it from there.
      if (!name && parts.length > 1 && extras.length > 0) name = extras.shift()
    } else {
      name = tokens.join(' ')
    }
  }
  email = email.trim().toLowerCase().replace(/^["']|["']$/g, '')
  const words = name.replace(/["']/g, '').trim().split(/\s+/).filter(Boolean)
  if (!email) return { error: 'no email', line: text }
  if (!EMAIL_RE.test(email)) return { error: 'email looks wrong', line: text }
  if (words.length === 0) return { error: 'no name', line: text }
  if (words.length === 1) return { error: 'needs a last name', line: text }
  return { first_name: words[0], last_name: words.slice(1).join(' '), email, extras }
}

export function parsePeople(text) {
  const seen = new Set()
  return text
    .split(/\r?\n/)
    .map(parseLine)
    .filter(Boolean)
    .map((entry) => {
      if (entry.error) return entry
      if (seen.has(entry.email)) return { error: 'listed twice', line: entry.email }
      seen.add(entry.email)
      return entry
    })
}

// `prefill` — {key: value} from lib/variables.variablePrefill — is what the
// "same for everyone" fields open holding, exactly as on the single form.
export default function PasteStaffForm({
  spaceId,
  variables = [],
  prefill = {},
  onDone,
  onSingle,
  onCancel,
}) {
  const [text, setText] = useState('')
  // The "same for everyone" answers.
  const [values, setValues] = useState(() =>
    Object.fromEntries(variables.map((variable) => [variable.key, prefill[variable.key] ?? ''])),
  )
  const [overrides, setOverrides] = useState({}) // per email: { key: value }
  const [fieldErrors, setFieldErrors] = useState({}) // shared, by key; per row, by email
  const [submitting, setSubmitting] = useState(false)
  const [progress, setProgress] = useState(null) // { added, total }
  const [failed, setFailed] = useState([])
  const [serverError, setServerError] = useState(null)

  const entries = useMemo(() => parsePeople(text), [text])
  const ready = entries.filter((entry) => !entry.error)
  const bad = entries.filter((entry) => entry.error)

  // What one person's answer is: their own edit, else the column pasted on
  // their line, else the value set for everyone.
  function answer(person, variable, index) {
    const own = overrides[person.email]?.[variable.key]
    if (own !== undefined) return own
    const pasted = person.extras?.[index]
    if (pasted) return pasted
    return values[variable.key] || ''
  }

  function answersFor(person) {
    return Object.fromEntries(
      variables.map((variable, index) => [variable.key, answer(person, variable, index).trim()]),
    )
  }

  async function handleSubmit(event) {
    event.preventDefault()
    if (submitting || ready.length === 0) return
    const errors = {}
    for (const person of ready) {
      const answers = answersFor(person)
      const missing = variables.filter((variable) => variable.required && !answers[variable.key])
      if (missing.length > 0) {
        errors[person.email] = `${missing.map((variable) => variable.label).join(', ')} needed.`
      }
    }
    setFieldErrors(errors)
    setServerError(null)
    if (Object.keys(errors).length > 0) return

    setSubmitting(true)
    setFailed([])
    const refused = []
    let added = 0
    setProgress({ added, total: ready.length })
    for (const person of ready) {
      try {
        await api(`/api/v1/spaces/${spaceId}/staff`, {
          method: 'POST',
          body: {
            first_name: person.first_name,
            last_name: person.last_name,
            email: person.email,
            variables: answersFor(person),
          },
        })
        added += 1
        setProgress({ added, total: ready.length })
      } catch (err) {
        refused.push(`${person.first_name} ${person.last_name} — ${err.detail || 'refused'}`)
      }
    }
    setSubmitting(false)
    if (refused.length === 0) {
      onDone(added)
      return
    }
    setFailed(refused)
    setServerError(
      `Added ${added} of ${ready.length}. The rest could not be added — fix the lines below and try again.`,
    )
    // Keep only the lines that failed, so a retry doesn't add anyone twice.
    const failedEmails = new Set(
      ready
        .filter((person) => refused.some((line) => line.startsWith(`${person.first_name} ${person.last_name}`)))
        .map((person) => person.email),
    )
    setText(
      entries
        .filter((entry) => entry.error || failedEmails.has(entry.email))
        .map((entry) =>
          entry.error
            ? entry.line
            : [`${entry.first_name} ${entry.last_name} <${entry.email}>`, ...(entry.extras ?? [])].join(', '),
        )
        .join('\n'),
    )
  }

  return (
    <div className="card card--elevated" style={{ marginBottom: 'var(--space-m)' }}>
      <div className="flex items-baseline justify-between flex-wrap" style={{ gap: 'var(--space-xs)', marginBottom: 'var(--space-s)' }}>
        <h3 className="text-h5">Paste a list</h3>
        <button type="button" className="text-btn" onClick={onSingle}>
          Add one at a time instead
        </button>
      </div>

      {serverError && (
        <div className="alert alert--error text-body-sm" style={{ marginBottom: 'var(--space-s)' }} role="alert">
          <div>
            <p>{serverError}</p>
            {failed.length > 0 && (
              <ul style={{ marginTop: 'var(--space-2xs)' }}>
                {failed.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} noValidate className="flex flex-col" style={{ gap: 'var(--space-s)' }}>
        <div className="field">
          <label className="field__label" htmlFor="paste-people">
            Who should we invite?
          </label>
          <textarea
            id="paste-people"
            rows={7}
            className="field__input paste-input"
            placeholder={'One person per line, for example:\nJane Doe <jane@acme.com>\nSam Rivers, sam@acme.com'}
            value={text}
            onChange={(event) => setText(event.target.value)}
            disabled={submitting}
          />
          <p className="field__help">
            Copy names and emails from an email, Teams or a spreadsheet. A name and an email on
            each line is all it needs
            {variables.length > 0
              ? `; add the ${variables[0].label.toLowerCase()} after the email to set it per line.`
              : '.'}
          </p>
        </div>

        {variables.length > 0 && (
          <div className="flex flex-col" style={{ gap: 'var(--space-s)' }}>
            <p className="field__help">Same for everyone, unless specified above:</p>
            {variables.map((variable) => (
              <div className="field" key={variable.key}>
                <label className="field__label" htmlFor={`paste-var-${variable.key}`}>
                  {variable.label}
                  {!variable.required && (
                    <span style={{ color: 'var(--color-text-tertiary)' }}> (optional)</span>
                  )}
                </label>
                <input
                  id={`paste-var-${variable.key}`}
                  type="text"
                  className="field__input"
                  value={values[variable.key] || ''}
                  onChange={(event) =>
                    setValues((current) => ({ ...current, [variable.key]: event.target.value }))
                  }
                  disabled={submitting}
                />
                {variable.help && (
                  <p className="text-caption" style={{ color: 'var(--color-text-tertiary)' }}>
                    {variable.help}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}

        {bad.length > 0 && (
          <div className="chips" aria-live="polite">
            {bad.map((entry, index) => (
              <span key={index} className="chip chip--error" title={entry.line}>
                {entry.line.length > 28 ? `${entry.line.slice(0, 28)}…` : entry.line} · {entry.error}
              </span>
            ))}
          </div>
        )}

        {ready.length > 0 && (
          <ul className="paste-rows" aria-label="People to add">
            {ready.map((person) => (
              <li key={person.email} className="paste-row">
                <span className="paste-row__who">
                  <span className="paste-row__name">
                    {person.first_name} {person.last_name}
                  </span>
                  <span className="paste-row__email">{person.email}</span>
                </span>
                {variables.map((variable, index) => (
                  <input
                    key={variable.key}
                    type="text"
                    className={`field__input paste-row__var${fieldErrors[person.email] ? ' field__input--invalid' : ''}`}
                    aria-label={`${variable.label} for ${person.first_name} ${person.last_name}`}
                    placeholder={variable.label}
                    value={answer(person, variable, index)}
                    onChange={(event) =>
                      setOverrides((current) => ({
                        ...current,
                        [person.email]: { ...current[person.email], [variable.key]: event.target.value },
                      }))
                    }
                    disabled={submitting}
                  />
                ))}
                {fieldErrors[person.email] && (
                  <span className="field__error paste-row__error">{fieldErrors[person.email]}</span>
                )}
              </li>
            ))}
          </ul>
        )}
        {entries.length > 0 && (
          <p className="text-body-sm" style={{ color: 'var(--color-text-secondary)' }}>
            {ready.length} ready
            {bad.length > 0 && (
              <>
                {' · '}
                <span style={{ color: 'var(--color-text-error)' }}>
                  {bad.length} {bad.length === 1 ? 'line needs' : 'lines need'} fixing
                </span>
              </>
            )}
          </p>
        )}

        <p className="text-body-sm" style={{ color: 'var(--color-text-secondary)' }}>
          Each person is added as not sent. Nothing goes out until you press Send.
        </p>

        <div className="flex flex-wrap" style={{ gap: 'var(--space-xs)' }}>
          <button type="submit" className="btn btn--primary" disabled={submitting || ready.length === 0}>
            {submitting
              ? `Adding ${progress?.added ?? 0} of ${progress?.total ?? ready.length}…`
              : `Add ${ready.length} ${ready.length === 1 ? 'person' : 'people'}`}
          </button>
          <button type="button" className="btn btn--secondary" onClick={onCancel} disabled={submitting}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  )
}
